"use client";

import React, { useState, useEffect, useRef } from "react";
import { Palette, Pipette } from "lucide-react";

export function ColorPicker({ onColorChange, currentColor, onOpenChange, trigger }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(currentColor?.value || '#FFFFFF');
  const [tempColor, setTempColor] = useState(currentColor?.value || '#FFFFFF');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const colorPickerRef = useRef(null);
  const colorInputRef = useRef(null);

  // Update selected color when currentColor prop changes
  useEffect(() => {
    if (currentColor?.value) {
      setSelectedColor(currentColor.value);
      setTempColor(currentColor.value);
    }
  }, [currentColor]);

  // Notify parent when open state changes
  const handleOpenChange = (open) => {
    setIsOpen(open);
    onOpenChange?.(open);
    if (!open) {
      // Reset temp color to selected when closing
      setTempColor(selectedColor);
      setShowCustomPicker(false);
    }
  };

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        handleOpenChange(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Preset colors for quick selection
  const presetColors = [
    '#FFFFFF', // white
    '#FEF08A', // yellow
    '#FED7AA', // orange
    '#FECACA', // pink
    '#F9A8D4', // rose
    '#DDD6FE', // purple
    '#BFDBFE', // blue
    '#BBF7D0', // green
    '#E5E7EB', // gray
    '#CBD5E1', // slate
  ];

  const handleColorChange = (color) => {
    setTempColor(color);
  };

  const handleColorApply = () => {
    setSelectedColor(tempColor);
    const colorObj = {
      name: 'custom',
      value: tempColor
    };
    console.log('ColorPicker: Color applied:', colorObj);
    onColorChange(colorObj);
    handleOpenChange(false);
  };

  const handlePresetColorSelect = (color) => {
    setTempColor(color);
    setSelectedColor(color);
    const colorObj = {
      name: 'custom',
      value: color
    };
    console.log('ColorPicker: Preset color selected:', colorObj);
    onColorChange(colorObj);
    // Don't close the popup when selecting a quick color
  };


  return (
    <div className="relative" ref={colorPickerRef} data-no-dnd="true">
      {trigger ? (
        <div onClick={() => handleOpenChange(!isOpen)} data-no-dnd="true">
          {trigger}
        </div>
      ) : (
        <button
          onClick={() => handleOpenChange(!isOpen)}
          className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-all duration-200"
          title="Card Colour"
          data-no-dnd="true"
        >
          <Palette className="w-4 h-4 text-gray-600" />
        </button>
      )}

      <div
        className={`absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl p-4 z-50 min-w-[240px] pointer-events-auto transition-all duration-200 ease-out ${
          isOpen
            ? 'opacity-100 scale-100 transform translate-y-0'
            : 'opacity-0 scale-95 transform -translate-y-2 pointer-events-none'
        }`}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-medium text-gray-700 mb-3">Card Colour</div>

        {/* Color Input Section */}
        <div className="space-y-3 mb-3">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={tempColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="h-10 w-10 border border-gray-300 rounded cursor-pointer"
              title="Click to choose color"
            />
            <input
              type="text"
              value={tempColor}
              onChange={(e) => {
                const value = e.target.value;
                if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value) || value === '' || value === '#') {
                  handleColorChange(value);
                }
              }}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-gray-400"
              placeholder="#FFFFFF"
            />
            <button
              onClick={handleColorApply}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Preset Colors */}
        <div className="border-t pt-3">
          <div className="text-xs text-gray-500 mb-2">Quick Colors</div>
          <div className="grid grid-cols-5 gap-2">
            {presetColors.map((color, index) => (
              <button
                key={index}
                onClick={() => handlePresetColorSelect(color)}
                className={`w-8 h-8 rounded border-2 hover:border-gray-400 transition-colors relative ${
                  color === '#FFFFFF' ? 'border-gray-300' : 'border-gray-200'
                } ${selectedColor === color ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                style={{ backgroundColor: color }}
                title={color}
              >
                {selectedColor === color && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}